import { useEffect, useState } from "react";
import { toast } from "sonner";
import { DecoderHeader } from "@/components/DecoderHeader";
import { UploadPanel } from "@/components/UploadPanel";
import { DecodingAnimation } from "@/components/DecodingAnimation";
import { BentoStats } from "@/components/BentoStats";
import { PromptLyricsPanel } from "@/components/PromptLyricsPanel";
import { HistoryList } from "@/components/HistoryList";
import { StructureTimeline } from "@/components/StructureTimeline";
import { PricingDialog } from "@/components/PricingDialog";
import { RatingDialog } from "@/components/RatingDialog";
import { BatchResults } from "@/components/BatchResults";
import { useAuth } from "@/context/AuthContext";
import { analyzeFile, analyzeUrl, listSongs, deleteSong } from "@/lib/api";

const BATCH_CONCURRENCY = 2;

export default function Decoder() {
  const { user, refresh: refreshUser } = useAuth();
  const [pricingOpen, setPricingOpen] = useState(false);
  const [pendingRun, setPendingRun] = useState(null);
  const [batch, setBatch] = useState([]);
  const [batchRunning, setBatchRunning] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [songs, setSongs] = useState([]);
  const [loadingLabel, setLoadingLabel] = useState("decoding audio signature");

  const refresh = async () => {
    try {
      const data = await listSongs();
      setSongs(data);
    } catch {
      /* silent */
    }
  };

  useEffect(() => { refresh(); }, []);

  const remaining = (user?.free_decodes_left || 0) + (user?.credits || 0);
  const canDecode = remaining > 0;

  // Every decode asks for a lyrics rating first; the chosen action runs afterwards
  const askRating = (count, run) => {
    if (!canDecode) return setPricingOpen(true);
    if (count > remaining) {
      setPricingOpen(true);
      return toast.error(`You have ${remaining} decode${remaining === 1 ? "" : "s"} left — remove some songs or buy a pack.`);
    }
    setPendingRun({ count, run });
  };

  const chooseRating = (rating) => {
    const run = pendingRun?.run;
    setPendingRun(null);
    run?.(rating);
  };

  const runDecode = async (fn, label) => {
    setBatch([]);
    setLoading(true);
    setAnalysis(null);
    setLoadingLabel(label);
    try {
      setAnalysis(await fn());
      toast.success("Decode complete — prompt + lyrics ready");
      refresh();
    } catch (e) {
      if (e?.response?.status === 402) setPricingOpen(true);
      toast.error(e?.response?.data?.detail || "Analysis failed");
    } finally {
      setLoading(false);
      refreshUser();
    }
  };

  const runBatch = async (files, rating) => {
    setAnalysis(null);
    setBatchRunning(true);
    const items = files.map((f, i) => ({ id: `${Date.now()}-${i}`, name: f.name, status: "queued" }));
    setBatch(items);
    const update = (idx, patch) => setBatch((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
    let next = 0;
    let failed = 0;
    let outOfDecodes = false;
    const worker = async () => {
      while (next < files.length) {
        const idx = next++;
        update(idx, { status: "running" });
        try {
          update(idx, { status: "done", result: await analyzeFile(files[idx], rating) });
        } catch (e) {
          failed++;
          if (e?.response?.status === 402) outOfDecodes = true;
          update(idx, { status: "error", error: e?.response?.data?.detail || "Analysis failed" });
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(BATCH_CONCURRENCY, files.length) }, worker));
    setBatchRunning(false);
    if (!failed) toast.success(`Batch finished — ${files.length} songs decoded`);
    else toast.error(`${files.length - failed} decoded, ${failed} failed — hover a failed row for the reason`);
    if (outOfDecodes) setPricingOpen(true);
    refresh();
    refreshUser();
  };

  const runFiles = (files) =>
    askRating(files.length, (rating) =>
      files.length === 1
        ? runDecode(() => analyzeFile(files[0], rating), "decoding audio + writing lyrics")
        : runBatch(files, rating));
  const runUrl = (url) =>
    askRating(1, (rating) => runDecode(() => analyzeUrl(url, rating), "fetching + decoding stream"));

  const removeSong = async (id) => {
    try {
      await deleteSong(id);
      if (analysis?.id === id) setAnalysis(null);
      toast.success("Deleted");
      refresh();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] grid-bg" data-testid="decoder-page">
      <DecoderHeader onUpgrade={() => setPricingOpen(true)} />
      <PricingDialog open={pricingOpen} onOpenChange={setPricingOpen} outOfDecodes={!canDecode} />
      <RatingDialog open={!!pendingRun} onOpenChange={(o) => !o && setPendingRun(null)} onChoose={chooseRating} count={pendingRun?.count} />

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
        {/* Hero */}
        <section className="mb-10 lg:mb-14 max-w-3xl">
          <div className="font-mono-display text-[11px] tracking-[0.35em] uppercase text-emerald-500 mb-4">
            [ audio :: reverse_engineered ]
          </div>
          <h1 className="font-mono-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] mb-5">
            Decode any song.<br />
            <span className="text-emerald-500">Get the Suno prompt.</span>
          </h1>
          <p className="text-base sm:text-lg text-zinc-400 leading-relaxed max-w-2xl">
            Upload an audio file or paste a YouTube link. We analyze the sonic DNA —
            genre, BPM, key, instruments, vocals, structure — and hand you a
            copy-paste-ready Suno prompt plus original lyrics in the same style.
          </p>
        </section>

        {/* Main grid */}
        <div className="grid lg:grid-cols-12 gap-6">
          {/* Left column */}
          <div className="lg:col-span-4 xl:col-span-3 space-y-6">
            <UploadPanel onAnalyzeFiles={runFiles} onAnalyzeUrl={runUrl} loading={loading || batchRunning} />
            <HistoryList
              songs={songs}
              activeId={analysis?.id}
              onSelect={setAnalysis}
              onDelete={removeSong}
            />
          </div>

          {/* Right column */}
          <div className="lg:col-span-8 xl:col-span-9 space-y-6">
            {loading && <DecodingAnimation label={loadingLabel} />}

            {batch.length > 0 && <BatchResults items={batch} onOpen={setAnalysis} onClose={batchRunning ? null : () => setBatch([])} />}

            {!loading && !analysis && !batch.length && (
              <div className="border border-white/10 bg-zinc-900/30 rounded-sm p-10 lg:p-14">
                <div className="font-mono-display text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-3">
                  output // idle
                </div>
                <h2 className="font-mono-display text-2xl sm:text-3xl font-bold tracking-tight text-white mb-3">
                  Waiting for a signal.
                </h2>
                <p className="text-zinc-400 max-w-lg leading-relaxed">
                  Drop a track or paste a link on the left. Analysis usually takes
                  <span className="text-emerald-500"> 20–40 seconds</span> depending on file size.
                </p>
                <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    ["genre", "detect"],
                    ["bpm", "estimate"],
                    ["mood", "map"],
                    ["prompt", "generate"],
                  ].map(([a, b]) => (
                    <div key={a} className="border border-white/10 p-4 rounded-sm">
                      <div className="font-mono-display text-[10px] tracking-[0.3em] uppercase text-emerald-500 mb-1">{a}</div>
                      <div className="font-mono-display text-sm text-zinc-400">{b}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!loading && analysis && (
              <>
                {/* Track header */}
                <div className="border border-white/10 bg-zinc-900/40 p-6 rounded-sm" data-testid="track-header">
                  <div className="font-mono-display text-[10px] tracking-[0.3em] uppercase text-emerald-500 mb-2">
                    decoded // track
                  </div>
                  <h2 className="font-mono-display text-2xl sm:text-3xl font-bold tracking-tight text-white break-words">
                    {analysis.title}
                  </h2>
                  <div className="mt-2 font-mono-display text-[10px] tracking-[0.2em] uppercase text-zinc-500 truncate">
                    source: {analysis.source_type} · {analysis.source_ref}
                  </div>
                </div>

                <BentoStats analysis={analysis} />
                <StructureTimeline structure={analysis.structure} />
                <PromptLyricsPanel key={analysis.id} analysis={analysis} />
              </>
            )}
          </div>
        </div>
      </main>

      <footer className="border-t border-white/10 mt-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="font-mono-display text-[10px] tracking-[0.3em] uppercase text-zinc-500">
            song2suno · v1.0
          </div>
          <div className="font-mono-display text-[10px] tracking-[0.3em] uppercase text-zinc-500">
            powered by gemini 3.1 pro
          </div>
        </div>
      </footer>
    </div>
  );
}
